import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsTask, fmtDate } from './api';
import { inputCls, btnPrimary } from './shared';

const entityPath = (t: AtsTask) => {
  if (!t.entity_type || !t.entity_id) return null;
  const base: Record<string, string> = { client: '/ats/clients', role: '/ats/roles', person: '/ats/people', placement: '/ats/placements', deal: '/ats/deals' };
  const b = base[t.entity_type];
  return b ? (t.entity_type === 'placement' || t.entity_type === 'deal' ? b : `${b}/${t.entity_id}`) : null;
};

const AtsTasks: React.FC = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState<AtsTask[]>([]);
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    atsFetch(token, `/tasks?status=${tab}`)
      .then((d) => setTasks(d.tasks))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, tab]);

  useEffect(load, [load]);

  const add = async () => {
    if (!token || !title.trim()) return;
    await atsFetch(token, '/tasks', { method: 'POST', body: JSON.stringify({ title: title.trim(), dueAt: due || null }) }).catch(() => {});
    setTitle(''); setDue('');
    load();
  };

  const setStatus = async (id: number, status: 'open' | 'done') => {
    if (!token) return;
    await atsFetch(token, `/tasks/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }).catch(() => {});
    load();
  };

  return (
    <div className="max-w-3xl">
      <div className="flex gap-2 mb-4">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} placeholder="Add a task…" className={inputCls} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm text-gray-600" />
        <button onClick={add} disabled={!title.trim()} className={btnPrimary}>Add</button>
      </div>

      <div className="flex gap-1 mb-3">
        {(['open', 'done'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{tab === 'open' ? 'Nothing open.' : 'Nothing completed yet.'}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-50">
          {tasks.map((t) => {
            const overdue = tab === 'open' && t.due_at && new Date(t.due_at).getTime() < Date.now();
            const link = entityPath(t);
            return (
              <div key={t.id} className="px-4 py-3 flex items-center gap-3 group">
                <button
                  onClick={() => setStatus(t.id, tab === 'open' ? 'done' : 'open')}
                  title={tab === 'open' ? 'Mark done' : 'Reopen'}
                  className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 ${tab === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${tab === 'done' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</p>
                  {t.details && <p className="text-xs text-gray-400 truncate">{t.details}</p>}
                </div>
                {link && <Link to={link} className="text-[11px] text-blue-600 hover:text-blue-700 capitalize">{t.entity_type} ↗</Link>}
                {t.due_at && <span className={`text-xs ${overdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>{fmtDate(t.due_at)}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AtsTasks;
